import React, { useState, Component } from 'react';
import { Filter, Star, User, Bot, Clock, MessageSquare, CheckCircle, XCircle, ExternalLink, UserCircle, Check } from 'lucide-react';
type RequestSource = 'user' | 'ai' | 'my';
type RequestStatus = 'new' | 'viewed' | 'responded' | 'dismissed' | 'resolved';
type RequestCategory = 'technical' | 'leadership' | 'communication' | 'product' | 'design' | 'career';
interface Respondent {
  id: string;
  name: string;
  role: string;
  matchScore: number;
}
interface BeaconRequest {
  id: string;
  title: string;
  summary: string;
  source: RequestSource;
  category: RequestCategory;
  timestamp: Date;
  status: RequestStatus;
  requester?: string;
  requesterRole?: string;
  matchScore?: number;
  respondents?: Respondent[];
}
const BeaconDashboard = () => {
  const [filter, setFilter] = useState<'all' | 'user' | 'ai' | 'my'>('all');
  const [categoryFilter, setCategoryFilter] = useState<RequestCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<RequestStatus | 'all'>('all');
  const [requests, setRequests] = useState<BeaconRequest[]>([{
    id: '1',
    title: 'React Component Architecture Help',
    summary: 'Looking for advice on structuring a large React application with complex state management',
    source: 'user',
    category: 'technical',
    timestamp: new Date(Date.now() - 3600000),
    status: 'new',
    requester: 'Alex Johnson',
    requesterRole: 'Frontend Developer',
    matchScore: 95
  }, {
    id: '2',
    title: 'Leadership Mentoring Opportunity',
    summary: 'Junior developer seeking guidance on leading their first team project',
    source: 'user',
    category: 'leadership',
    timestamp: new Date(Date.now() - 7200000),
    status: 'viewed',
    requester: 'Jamie Smith',
    requesterRole: 'Junior Developer',
    matchScore: 87
  }, {
    id: '3',
    title: 'TypeScript Workshop Suggestion',
    summary: 'Based on your expertise, you could host a workshop on TypeScript best practices',
    source: 'ai',
    category: 'technical',
    timestamp: new Date(Date.now() - 86400000),
    status: 'new',
    matchScore: 92
  }, {
    id: '4',
    title: 'Code Review Request',
    summary: 'Need help reviewing a complex algorithm implementation for performance optimization',
    source: 'user',
    category: 'technical',
    timestamp: new Date(Date.now() - 43200000),
    status: 'new',
    requester: 'Taylor Wong',
    requesterRole: 'Backend Developer',
    matchScore: 89
  }, {
    id: '5',
    title: 'Cross-team Communication Workshop',
    summary: 'Your communication skills could help bridge the gap between design and development teams',
    source: 'ai',
    category: 'communication',
    timestamp: new Date(Date.now() - 172800000),
    status: 'viewed',
    matchScore: 84
  }, {
    id: '6',
    title: 'Product Requirements Clarification',
    summary: 'Need help translating business requirements into technical specifications',
    source: 'user',
    category: 'product',
    timestamp: new Date(Date.now() - 28800000),
    status: 'responded',
    requester: 'Jordan Lee',
    requesterRole: 'Product Manager',
    matchScore: 78
  }, {
    id: '7',
    title: 'Career Transition Advice',
    summary: 'Looking for guidance on moving from development to technical product management',
    source: 'user',
    category: 'career',
    timestamp: new Date(Date.now() - 259200000),
    status: 'new',
    requester: 'Morgan Chen',
    requesterRole: 'Senior Developer',
    matchScore: 91
  }, {
    id: '8',
    title: 'UI Design Feedback Opportunity',
    summary: 'Your technical perspective would be valuable for a new design system implementation',
    source: 'ai',
    category: 'design',
    timestamp: new Date(Date.now() - 129600000),
    status: 'dismissed',
    matchScore: 76
  }, {
    id: '9',
    title: 'Help with GraphQL Implementation',
    summary: 'I need help setting up a GraphQL API for our frontend application',
    source: 'my',
    category: 'technical',
    timestamp: new Date(Date.now() - 172800000),
    status: 'new',
    respondents: []
  }, {
    id: '10',
    title: 'Feedback on My Leadership Approach',
    summary: 'Looking for constructive feedback on how I handled a recent team conflict',
    source: 'my',
    category: 'leadership',
    timestamp: new Date(Date.now() - 345600000),
    status: 'responded',
    respondents: [{
      id: 'resp1',
      name: 'Sarah Miller',
      role: 'Engineering Manager',
      matchScore: 92
    }, {
      id: 'resp2',
      name: 'David Chen',
      role: 'Senior Team Lead',
      matchScore: 87
    }]
  }, {
    id: '11',
    title: 'Best Practices for Microservices',
    summary: 'I need advice on structuring microservices for our growing application',
    source: 'my',
    category: 'technical',
    timestamp: new Date(Date.now() - 259200000),
    status: 'responded',
    respondents: [{
      id: 'resp3',
      name: 'Michael Rodriguez',
      role: 'Principal Architect',
      matchScore: 95
    }]
  }]);
  const handleStatusChange = (requestId: string, newStatus: RequestStatus) => {
    setRequests(requests.map(request => request.id === requestId ? {
      ...request,
      status: newStatus
    } : request));
  };
  const resolveRequest = (requestId: string, respondentId: string) => {
    setRequests(requests.map(request => request.id === requestId ? {
      ...request,
      status: 'resolved'
      // We could also track which respondent was selected here
    } : request));
  };
  const getCategoryColor = (category: RequestCategory) => {
    const colors = {
      technical: 'bg-blue-100 text-blue-800',
      leadership: 'bg-purple-100 text-purple-800',
      communication: 'bg-green-100 text-green-800',
      product: 'bg-yellow-100 text-yellow-800',
      design: 'bg-pink-100 text-pink-800',
      career: 'bg-indigo-100 text-indigo-800'
    };
    return colors[category];
  };
  const getCategoryIcon = (category: RequestCategory) => {
    switch (category) {
      case 'technical':
        return <code className="text-xs mr-1" data-id="element-67">{'</>'}</code>;
      case 'leadership':
        return <Star className="w-3 h-3 mr-1" data-id="element-68" />;
      case 'communication':
        return <MessageSquare className="w-3 h-3 mr-1" data-id="element-69" />;
      case 'product':
        return <ExternalLink className="w-3 h-3 mr-1" data-id="element-70" />;
      case 'design':
        return <span className="font-bold mr-1" data-id="element-71">Aa</span>;
      case 'career':
        return <Clock className="w-3 h-3 mr-1" data-id="element-72" />;
      default:
        return null;
    }
  };
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  };
  const filteredRequests = requests.filter(request => {
    const sourceMatch = filter === 'all' || request.source === filter;
    const categoryMatch = categoryFilter === 'all' || request.category === categoryFilter;
    const statusMatch = statusFilter === 'all' || request.status === statusFilter;
    return sourceMatch && categoryMatch && statusMatch;
  });
  const hasAnsweredRequests = requests.some(request => request.source === 'my' && request.status === 'responded' && request.respondents && request.respondents.length > 0);
  return <div className="flex flex-col h-full border rounded-lg bg-white shadow-sm overflow-hidden" data-id="element-73">
      <div className="px-4 py-3 border-b bg-gray-50" data-id="element-74">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-75">Beacon</h2>
        <p className="text-sm text-gray-500" data-id="element-76">
          Discover opportunities to help others and share your expertise
        </p>
      </div>
      {/* Filters */}
      <div className="border-b px-4 py-2 bg-gray-50" data-id="element-77">
        <div className="flex flex-wrap items-center gap-3" data-id="element-78">
          <div className="flex items-center" data-id="element-79">
            <Filter className="w-4 h-4 text-gray-500 mr-2" data-id="element-80" />
            <span className="text-sm text-gray-700" data-id="element-81">Source:</span>
          </div>
          <div className="flex space-x-2" data-id="element-82">
            <button onClick={() => setFilter('all')} className={`px-3 py-1 text-xs rounded-full ${filter === 'all' ? 'bg-blue-100 text-blue-800 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-83">
              All
            </button>
            <button onClick={() => setFilter('my')} className={`px-3 py-1 text-xs rounded-full flex items-center ${filter === 'my' ? 'bg-blue-100 text-blue-800 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-84">
              <UserCircle className="w-3 h-3 mr-1" data-id="element-85" />
              My Requests
              {hasAnsweredRequests && <span className="ml-1 w-2 h-2 bg-green-500 rounded-full" data-id="element-86"></span>}
            </button>
            <button onClick={() => setFilter('user')} className={`px-3 py-1 text-xs rounded-full flex items-center ${filter === 'user' ? 'bg-blue-100 text-blue-800 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-87">
              <User className="w-3 h-3 mr-1" data-id="element-88" />
              User Requests
            </button>
            <button onClick={() => setFilter('ai')} className={`px-3 py-1 text-xs rounded-full flex items-center ${filter === 'ai' ? 'bg-blue-100 text-blue-800 font-medium' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-89">
              <Bot className="w-3 h-3 mr-1" data-id="element-90" />
              AI Suggestions
            </button>
          </div>
          <div className="flex items-center ml-2" data-id="element-91">
            <span className="text-sm text-gray-700 mr-2" data-id="element-92">Category:</span>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as RequestCategory | 'all')} className="text-xs border rounded-md px-2 py-1 bg-white" data-id="element-93">
              <option value="all" data-id="element-94">All Categories</option>
              <option value="technical" data-id="element-95">Technical</option>
              <option value="leadership" data-id="element-96">Leadership</option>
              <option value="communication" data-id="element-97">Communication</option>
              <option value="product" data-id="element-98">Product</option>
              <option value="design" data-id="element-99">Design</option>
              <option value="career" data-id="element-100">Career</option>
            </select>
          </div>
          <div className="flex items-center ml-2" data-id="element-101">
            <span className="text-sm text-gray-700 mr-2" data-id="element-102">Status:</span>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as RequestStatus | 'all')} className="text-xs border rounded-md px-2 py-1 bg-white" data-id="element-103">
              <option value="all" data-id="element-104">All Status</option>
              <option value="new" data-id="element-105">New</option>
              <option value="viewed" data-id="element-106">Viewed</option>
              <option value="responded" data-id="element-107">Responded</option>
              <option value="resolved" data-id="element-108">Resolved</option>
              <option value="dismissed" data-id="element-109">Dismissed</option>
            </select>
          </div>
        </div>
      </div>
      {/* Request List */}
      <div className="flex-1 overflow-y-auto" data-id="element-110">
        {filteredRequests.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-gray-500" data-id="element-111">
            <Filter className="w-12 h-12 mb-2 text-gray-300" data-id="element-112" />
            <p data-id="element-113">No matching requests found</p>
            <button onClick={() => {
          setFilter('all');
          setCategoryFilter('all');
          setStatusFilter('all');
        }} className="mt-2 text-blue-600 text-sm hover:underline" data-id="element-114">
              Clear filters
            </button>
          </div> : <div className="divide-y" data-id="element-115">
            {filteredRequests.map(request => <div key={request.id} className={`p-4 hover:bg-gray-50 transition-colors ${request.status === 'new' ? 'border-l-4 border-blue-500' : ''} ${request.source === 'my' && request.status === 'responded' ? 'border-l-4 border-green-500' : ''} ${request.source === 'my' ? 'bg-blue-50 hover:bg-blue-100' : ''}`} data-id="element-116">
                <div className="flex justify-between items-start mb-2" data-id="element-117">
                  <div className="flex items-start" data-id="element-118">
                    <div className={`p-2 rounded-full mr-3 ${request.source === 'user' ? 'bg-blue-50 text-blue-600' : request.source === 'my' ? 'bg-indigo-50 text-indigo-600' : 'bg-purple-50 text-purple-600'}`} data-id="element-119">
                      {request.source === 'user' ? <User className="w-5 h-5" data-id="element-120" /> : request.source === 'my' ? <UserCircle className="w-5 h-5" data-id="element-121" /> : <Bot className="w-5 h-5" data-id="element-122" />}
                    </div>
                    <div data-id="element-123">
                      <h3 className="font-medium text-gray-900" data-id="element-124">
                        {request.title}
                        {request.source === 'my' && <span className="ml-2 text-xs font-normal text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full" data-id="element-125">
                            My Request
                          </span>}
                      </h3>
                      <div className="flex items-center mt-1 space-x-2" data-id="element-126">
                        <span className={`text-xs px-2 py-0.5 rounded-full flex items-center ${getCategoryColor(request.category)}`} data-id="element-127">
                          {getCategoryIcon(request.category)}
                          {request.category.charAt(0).toUpperCase() + request.category.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center" data-id="element-128">
                          <Clock className="w-3 h-3 mr-1" data-id="element-129" />
                          {formatTimeAgo(request.timestamp)}
                        </span>
                        {request.matchScore && request.source !== 'my' && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-id="element-130">
                            {request.matchScore}% match
                          </span>}
                        {request.source === 'my' && request.respondents && request.respondents.length > 0 && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full" data-id="element-131">
                              {request.respondents.length} response
                              {request.respondents.length !== 1 ? 's' : ''}
                            </span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-132">
                    {request.source !== 'my' && request.status !== 'responded' && <button onClick={() => handleStatusChange(request.id, 'responded')} className="text-green-600 hover:bg-green-50 p-1 rounded" title="Respond" data-id="element-133">
                          <CheckCircle className="w-5 h-5" data-id="element-134" />
                        </button>}
                    {request.source !== 'my' && request.status !== 'dismissed' && <button onClick={() => handleStatusChange(request.id, 'dismissed')} className="text-red-600 hover:bg-red-50 p-1 rounded" title="Dismiss" data-id="element-135">
                          <XCircle className="w-5 h-5" data-id="element-136" />
                        </button>}
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-3" data-id="element-137">{request.summary}</p>
                {request.requester && request.source !== 'my' && <div className="flex items-center mt-2" data-id="element-138">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs mr-2" data-id="element-139">
                      {request.requester.charAt(0)}
                    </div>
                    <div className="text-xs" data-id="element-140">
                      <span className="font-medium" data-id="element-141">{request.requester}</span>
                      {request.requesterRole && <span className="text-gray-500" data-id="element-142">
                          {' '}
                          · {request.requesterRole}
                        </span>}
                    </div>
                  </div>}
                {/* Respondents for my requests */}
                {request.source === 'my' && request.respondents && request.respondents.length > 0 && <div className="mt-3 border-t pt-3" data-id="element-143">
                      <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-144">
                        People who can help:
                      </h4>
                      <div className="space-y-3" data-id="element-145">
                        {request.respondents.map(respondent => <div key={respondent.id} className="flex items-start justify-between bg-white p-2 rounded-md border" data-id="element-146">
                            <div className="flex items-center" data-id="element-147">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm mr-3" data-id="element-148">
                                {respondent.name.charAt(0)}
                              </div>
                              <div data-id="element-149">
                                <div className="font-medium text-sm" data-id="element-150">
                                  {respondent.name}
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-151">
                                  {respondent.role}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center" data-id="element-152">
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full mr-2" data-id="element-153">
                                {respondent.matchScore}% match
                              </span>
                              {request.status !== 'resolved' && <button onClick={() => resolveRequest(request.id, respondent.id)} className="bg-green-600 text-white text-xs px-2 py-1 rounded hover:bg-green-700 flex items-center" data-id="element-154">
                                  <Check className="w-3 h-3 mr-1" data-id="element-155" />
                                  Accept
                                </button>}
                            </div>
                          </div>)}
                      </div>
                    </div>}
                <div className="mt-3 flex justify-end" data-id="element-156">
                  <button className="text-blue-600 text-xs hover:text-blue-800 hover:underline" data-id="element-157">
                    View details
                  </button>
                </div>
              </div>)}
          </div>}
      </div>
      {/* Stats Footer */}
      <div className="border-t px-4 py-3 bg-gray-50 text-xs text-gray-500" data-id="element-158">
        Showing {filteredRequests.length} of {requests.length} requests ·{' '}
        {requests.filter(r => r.status === 'new').length} new ·{' '}
        {requests.filter(r => r.status === 'responded').length} responded ·{' '}
        {requests.filter(r => r.source === 'my' && r.respondents && r.respondents.length > 0).length}{' '}
        of my requests with responses
      </div>
    </div>;
};
export default BeaconDashboard;